import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Download, RefreshCw, Search } from 'lucide-react';
import * as hr from '../../api';
import { Select } from '../../components/Controls';
import Loader from '../../components/Loader';

// Pieces shared by the payroll attendance screens (Attendance Import, Attendance Manual, Site Sheets, Attendance Check,
// Attendance Grid) — Stephanet's pay15/shared and paysys/shared, on our controls. Every screen works on one payroll month
// (the payroll calendar's Date Control); the month picked on one screen stays picked on the others.

const f2 = new Intl.NumberFormat('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const f0 = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 2 });
export const n2 = v => (v === null || v === undefined || v === '' ? '—' : f2.format(Number(v)));
export const n0 = v => (v === null || v === undefined || v === '' ? '—' : f0.format(Number(v)));
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const plabel = p => { if (!p) return ''; const [y, m] = p.split('-'); return `${MONTHS[Number(m) - 1]} ${y}`; };
export const dmy = v => {
  if (!v) return '—';
  const d = new Date(`${String(v).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
export const words = q => (q || '').toLowerCase().split(/\s+/).filter(Boolean);
export const matches = (r, q, keys) => {
  const w = words(q);
  if (!w.length) return true;
  const s = keys.map(k => r[k] ?? '').join(' ').toLowerCase();
  return w.every(x => s.includes(x));
};
export const letters = n => { let s = ''; let x = n + 1; while (x) { const r = (x - 1) % 26; s = String.fromCharCode(65 + r) + s; x = Math.floor((x - 1) / 26); } return s; };

// ── the payroll month (shared between the screens) ──
let calCache = null;
let periodSel = null;

export function usePayrollMonth() {
  const [cal, setCal] = useState(calCache);
  const [period, setP] = useState(periodSel || '');
  useEffect(() => {
    if (calCache) { if (!period) setP(periodSel || calCache.active); return; }
    hr.getPayrollCalendar().then(c => { calCache = c; setCal(c); if (!period) setP(periodSel || c.active); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [cal, period, v => { periodSel = v; setP(v); }];
}

export function PeriodPick({ cal, value, onChange, label = 'Payroll Month' }) {
  const opts = useMemo(() => {
    const s = new Set(cal?.periods || []);
    if (cal?.active) {
      const [y, m] = cal.active.split('-').map(Number);
      for (let i = -3; i <= 12; i++) {
        const d = new Date(y, m - 1 - i, 1);
        s.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
    }
    return [...s].sort().reverse();
  }, [cal]);
  return (
    <label className="ps-field"><span>{label}</span>
      <Select value={value} onChange={e => onChange(e.target.value)} title="Payroll month">
        {opts.map(p => <option key={p} value={p}>{plabel(p)}{p === cal?.active ? ' (active)' : ''}</option>)}
      </Select>
    </label>
  );
}

export function PyHeader({ title, sub, cal, period, children }) {
  return (
    <div className="mdr-top ps-top py-top">
      <div>
        <h2>{title}</h2>
        {sub && <p>{sub}</p>}
        {period && <span className="py-period-tag">Payroll month: <b>{plabel(period)}</b>{period === cal?.active ? ' · active' : ''}</span>}
      </div>
      {children && <div className="ps-top-actions no-print">{children}</div>}
    </div>
  );
}

export function Sel({ label, value, onChange, options, all = 'All' }) {
  return (
    <label className="ps-field"><span>{label}</span>
      <Select value={value} onChange={e => onChange(e.target.value)} title={label}>
        {all !== null && <option value="">{all}</option>}
        {options.map(o => (typeof o === 'object' ? <option key={o.value} value={o.value}>{o.label}</option> : <option key={o} value={o}>{o}</option>))}
      </Select>
    </label>
  );
}

export function SearchBox({ value, onChange, placeholder = 'Search employee, number, site…' }) {
  return (
    <label className="ps-field ps-search"><span>Search</span>
      <div><Search size={14} /><input value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} /></div>
    </label>
  );
}

export const Panel = ({ title, note, children, className = '' }) => (
  <div className={`mdr-card ps-panel ${className}`}><div className="ps-panel-h"><h3>{title}</h3>{note && <span>{note}</span>}</div>{children}</div>
);

export const Stat = ({ label, value, tone }) => <div className={`py-stat ${tone ? `t-${tone}` : ''}`}><small>{label}</small><b>{value}</b></div>;

export const Loading = ({ what = 'attendance' }) => <Loader title={`Loading ${what}…`} subtitle="Fetching attendance records" />;
export const ErrorBox = ({ error }) => <div className="error">{error}</div>;

export function Toolbar({ onRefresh, onExport, children }) {
  return <>
    {children}
    {onRefresh && <button className="mdr-btn" onClick={onRefresh}><RefreshCw size={14} /> Refresh</button>}
    {onExport && <button className="mdr-btn" onClick={onExport}><Download size={14} /> Export</button>}
  </>;
}

// ── CSV ──
export function downloadCsv(filename, head, rows) {
  const q = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [head.map(q).join(','), ...rows.map(r => r.map(q).join(','))].join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 500);
}
export const cellText = (c, r) => { const v = typeof c.get === 'function' ? c.get(r) : r[c.key]; return v === null || v === undefined ? '' : v; };
export function exportRows(filename, cols, rows) {
  downloadCsv(filename, ['S/N', ...cols.map(c => c.label)], rows.map((r, i) => [i + 1, ...cols.map(c => cellText(c, r))]));
}

// ── the workbook table: frozen leading columns, click-to-sort, totals row ──
const isNum = c => ['money', 'num'].includes(c.kind);
const cell = (c, r) => {
  if (typeof c.render === 'function') return c.render(r);
  const v = cellText(c, r);
  if (c.kind === 'money') return n2(v);
  if (c.kind === 'num') return v === '' ? '—' : n0(v);
  return v === '' ? '—' : v;
};

export function Workbook({ cols, rows, frozen = 2, totals = true, onRow, empty = 'Nothing to show.', maxHeight = 560, rowClass }) {
  const [sort, setSort] = useState(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const c = cols.find(x => x.key === sort.key);
    return [...rows].sort((a, b) => {
      const x = cellText(c, a); const y = cellText(c, b);
      const d = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
      return sort.dir * d;
    });
  }, [rows, sort, cols]);
  const sums = useMemo(() => Object.fromEntries(cols.filter(c => c.kind === 'money' || (c.kind === 'num' && c.sum))
    .map(c => [c.key, rows.reduce((t, r) => t + (Number(cellText(c, r)) || 0), 0)])), [cols, rows]);
  const left = i => 44 + cols.slice(0, i).reduce((t, x) => t + (x.w || 120), 0);
  const frz = (c, i) => (i < frozen ? { left: left(i), minWidth: c.w || 120, maxWidth: c.w || 120 } : { minWidth: c.w });
  return (
    <div className="ps-wb" style={{ maxHeight }}>
      <table className="ps-table">
        <thead>
          <tr>
            <th className="fz ps-sn" style={{ left: 0 }}>S/N</th>
            {cols.map((c, i) => (
              <th key={c.key} className={`${i < frozen ? 'fz' : ''} ${isNum(c) ? 'r' : ''}`} style={frz(c, i)}
                  onClick={() => setSort(s => (s?.key === c.key ? (s.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }))}>
                {c.label}{sort?.key === c.key && (sort.dir === 1 ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, n) => (
            <tr key={r.id ?? r.row ?? n} className={`${onRow ? 'clickable' : ''} ${rowClass ? rowClass(r) : ''}`} onClick={onRow ? () => onRow(r) : undefined}>
              <td className="fz ps-sn" style={{ left: 0 }}>{n + 1}</td>
              {cols.map((c, i) => (
                <td key={c.key} className={`${i < frozen ? 'fz' : ''} ${isNum(c) ? 'r mono' : ''} ${c.cls || ''}`}
                    style={i < frozen ? frz(c, i) : undefined} title={i < frozen ? String(cellText(c, r)) : undefined}>{cell(c, r)}</td>
              ))}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length + 1} className="ps-empty">{empty}</td></tr>}
        </tbody>
        {totals && rows.length > 0 && <tfoot>
          <tr>
            <td className="fz ps-sn" style={{ left: 0 }} />
            {cols.map((c, i) => (
              <td key={c.key} className={`${i < frozen ? 'fz' : ''} ${isNum(c) ? 'r mono' : ''}`} style={i < frozen ? frz(c, i) : undefined}>
                {i === 0 ? `TOTAL (${rows.length})` : c.key in sums ? (c.kind === 'money' ? n2(sums[c.key]) : n0(sums[c.key])) : ''}
              </td>
            ))}
          </tr>
        </tfoot>}
      </table>
    </div>
  );
}

// a day header cell of the month tables: weekday over the day number, shaded by type (WD / RD / PH)
export const DayHead = ({ days, span = 1 }) => days.map(x => (
  <th key={x.date} colSpan={span} className={`c py-dh t-${x.type}`} title={x.holiday || ''}><small>{x.weekday}</small>{x.date.slice(8)}</th>
));
export const fmt = v => (v ? +Number(v).toFixed(2) : '');
export const SRC_LABEL = { manual: 'Manual', leave: 'Leave', biometric: 'Device', import: 'Imported' };
